/**
 * Parity Monitor Page
 * Scheduled StarRocks vs Warehouse row-count parity across every account, with history.
 *
 * The page leads with what CHANGED since the previous run — a stable gap is a known condition,
 * a moving one is an incident. Full counts, MV health and errors sit below in tabs. Every
 * difference carries a category explaining why the two engines legitimately disagree (events
 * ingest lead, loader semantics, by-design tables, parked tables, live skew) so that only
 * DIFFERS rows and changes need a human.
 */

import { Fragment, useMemo, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  Activity, AlertTriangle, ArrowDown, ArrowRight, ArrowUp, CheckCircle, ChevronDown, ChevronRight,
  Database, Play, RefreshCw, Scale, ServerCrash, XCircle,
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import { LoadingState, ErrorState } from '@/components/ui/loading-state'
import { cn, formatDate, formatNumber } from '@/lib/utils'
import { getLatestRun, getRun, getRuns, getTableHistory, triggerRun } from '../queries/parityMonitor'
import type { Change, MvRow, ParityCategory, RunDetail, RunHeader, Severity, TableRow } from '../types'

// ── Presentation helpers ──────────────────────────────────────────────────────

const CATEGORY_LABEL: Record<ParityCategory, string> = {
  MATCH: 'Match',
  DIFFERS: 'Differs',
  EVENTS: 'Events (ingest lead)',
  LOADER_SEMANTICS: 'Loader semantics',
  BY_DESIGN: 'By design',
  SET_ASIDE: 'Set aside',
  LIVE_SKEW: 'Live skew',
  ENGINE_ERROR: 'Count failed',
}

const CATEGORY_CLASS: Record<ParityCategory, string> = {
  MATCH: 'text-green-700 bg-green-50 border-green-200',
  DIFFERS: 'text-red-700 bg-red-50 border-red-200',
  EVENTS: 'text-sky-700 bg-sky-50 border-sky-200',
  LOADER_SEMANTICS: 'text-violet-700 bg-violet-50 border-violet-200',
  BY_DESIGN: 'text-gray-600 bg-gray-50 border-gray-200',
  SET_ASIDE: 'text-gray-600 bg-gray-50 border-gray-200',
  LIVE_SKEW: 'text-amber-700 bg-amber-50 border-amber-200',
  ENGINE_ERROR: 'text-orange-700 bg-orange-50 border-orange-200',
}

const CATEGORY_ORDER: ParityCategory[] = [
  'DIFFERS', 'ENGINE_ERROR', 'LOADER_SEMANTICS', 'SET_ASIDE', 'EVENTS', 'LIVE_SKEW', 'BY_DESIGN', 'MATCH',
]

const SEVERITY_CLASS: Record<Severity, string> = {
  HIGH: 'text-red-700 bg-red-50 border-red-200',
  MEDIUM: 'text-amber-700 bg-amber-50 border-amber-200',
  INFO: 'text-sky-700 bg-sky-50 border-sky-200',
}

const SEVERITY_RANK: Record<Severity, number> = { HIGH: 0, MEDIUM: 1, INFO: 2 }

function Pill({ className, children }: { className: string; children: React.ReactNode }) {
  return (
    <span className={cn('inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full border whitespace-nowrap', className)}>
      {children}
    </span>
  )
}

function Count({ value }: { value: number }) {
  return (
    <td className={cn('py-2 px-3 text-right font-mono text-xs tabular-nums', value < 0 && 'text-orange-600')}>
      {value < 0 ? 'failed' : formatNumber(value)}
    </td>
  )
}

function Diff({ diff, pct }: { diff: number; pct: number | null }) {
  if (diff === 0) return <td className="py-2 px-3 text-right font-mono text-xs text-green-700">=</td>
  return (
    <td className={cn('py-2 px-3 text-right font-mono text-xs tabular-nums', diff > 0 ? 'text-gray-800' : 'text-gray-800')}>
      {diff > 0 ? '+' : ''}{formatNumber(diff)}
      {pct != null && <span className="text-gray-400 ml-1">({pct > 0 ? '+' : ''}{pct.toFixed(pct === 0 ? 0 : Math.abs(pct) < 0.01 ? 4 : 2)}%)</span>}
    </td>
  )
}

/** Direction of |diff| since the previous run. */
function Trend({ row }: { row: TableRow }) {
  if (row.prevDiff == null) return <td className="py-2 px-3 text-center text-gray-300 text-xs">new</td>
  const before = Math.abs(row.prevDiff), after = Math.abs(row.diff)
  if (before === after) return <td className="py-2 px-3 text-center text-gray-400"><ArrowRight className="h-3 w-3 inline" /></td>
  const widened = after > before
  return (
    <td className={cn('py-2 px-3 text-center text-xs font-mono', widened ? 'text-red-600' : 'text-green-600')}>
      {widened ? <ArrowUp className="h-3 w-3 inline" /> : <ArrowDown className="h-3 w-3 inline" />}
      <span className="ml-1">{widened ? '+' : '−'}{formatNumber(Math.abs(after - before))}</span>
    </td>
  )
}

function Tile({ label, value, sub, tone = 'default', icon }: {
  label: string; value: string; sub?: string; tone?: 'default' | 'good' | 'bad' | 'warn'; icon: React.ReactNode
}) {
  return (
    <div className={cn('rounded-lg border p-3 flex items-start gap-3',
      tone === 'good' && 'border-green-200 bg-green-50/40',
      tone === 'bad' && 'border-red-200 bg-red-50/40',
      tone === 'warn' && 'border-amber-200 bg-amber-50/40',
      tone === 'default' && 'border-gray-200 bg-white')}>
      <div className="text-gray-400 mt-0.5">{icon}</div>
      <div>
        <div className="text-[11px] uppercase tracking-wide text-gray-500">{label}</div>
        <div className="text-lg font-semibold text-gray-900 leading-tight">{value}</div>
        {sub && <div className="text-[11px] text-gray-500 mt-0.5">{sub}</div>}
      </div>
    </div>
  )
}

// ── Changes feed ──────────────────────────────────────────────────────────────

function ChangesFeed({ changes }: { changes: Change[] }) {
  const sorted = useMemo(() => [...changes].sort((a, b) => SEVERITY_RANK[a.severity] - SEVERITY_RANK[b.severity]), [changes])
  if (sorted.length === 0) {
    return (
      <div className="rounded-lg border border-green-200 bg-green-50/40 p-4 flex items-center gap-2 text-sm text-green-800">
        <CheckCircle className="h-4 w-4" /> No changes since the previous run.
      </div>
    )
  }
  return (
    <div className="rounded-lg border border-gray-200 divide-y divide-gray-100">
      {sorted.map((c, i) => (
        <div key={i} className="px-4 py-2.5 flex items-start gap-3 text-sm">
          <Pill className={SEVERITY_CLASS[c.severity]}>{c.severity}</Pill>
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-baseline gap-x-2">
              <span className="font-mono text-xs text-gray-500">{c.type}</span>
              {c.accId != null && <span className="font-mono text-xs text-gray-500">acc_{c.accId}</span>}
              <span className="font-mono text-xs text-gray-900">{c.subject}</span>
            </div>
            <div className="text-gray-700">{c.detail}</div>
          </div>
          {(c.before != null || c.after != null) && (
            <div className="font-mono text-xs text-gray-500 whitespace-nowrap">
              {c.before != null ? formatNumber(c.before) : '—'} → {c.after != null ? formatNumber(c.after) : '—'}
            </div>
          )}
        </div>
      ))}
    </div>
  )
}

// ── Tables tab ────────────────────────────────────────────────────────────────

function HistoryRow({ accId, table, colSpan }: { accId: number; table: string; colSpan: number }) {
  const { data, isLoading } = useQuery({ queryKey: ['parity', 'history', accId, table], queryFn: () => getTableHistory(accId, table, 24) })
  return (
    <tr className="bg-gray-50/60">
      <td colSpan={colSpan} className="px-6 py-2">
        {isLoading ? <span className="text-xs text-gray-400">loading history…</span> : (
          <div className="flex flex-wrap gap-1.5">
            {(data ?? []).slice().reverse().map((h, i) => (
              <span key={i} title={`SR ${formatNumber(h.starrocksCount)} / WH ${formatNumber(h.warehouseCount)}`}
                className={cn('font-mono text-[11px] px-1.5 py-0.5 rounded border',
                  h.diff === 0 ? 'border-green-200 text-green-700 bg-white' : 'border-gray-200 text-gray-700 bg-white')}>
                {h.diff === 0 ? '=' : `${h.diff > 0 ? '+' : ''}${formatNumber(h.diff)}`}
              </span>
            ))}
            <span className="text-[11px] text-gray-400 self-center ml-1">oldest → newest</span>
          </div>
        )}
      </td>
    </tr>
  )
}

function TablesTab({ rows }: { rows: TableRow[] }) {
  const [category, setCategory] = useState<ParityCategory | 'ALL_DIFF' | 'ALL'>('ALL_DIFF')
  const [layer, setLayer] = useState<'all' | 'base' | 'aggregate'>('all')
  const [search, setSearch] = useState('')
  const [open, setOpen] = useState<string | null>(null)

  const counts = useMemo(() => {
    const c = {} as Record<ParityCategory, number>
    for (const r of rows) c[r.category] = (c[r.category] ?? 0) + 1
    return c
  }, [rows])

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase()
    return rows
      .filter((r) => category === 'ALL' ? true : category === 'ALL_DIFF' ? r.diff !== 0 || r.category === 'ENGINE_ERROR' : r.category === category)
      .filter((r) => layer === 'all' || r.layer === layer)
      .filter((r) => !q || r.table.includes(q) || String(r.accId).includes(q))
      .sort((a, b) => Math.abs(b.diff) - Math.abs(a.diff))
  }, [rows, category, layer, search])

  const chip = (key: ParityCategory | 'ALL_DIFF' | 'ALL', label: string, n: number) => (
    <button key={key} onClick={() => setCategory(key)}
      className={cn('text-xs px-2.5 py-1 rounded-full border transition-colors',
        category === key ? 'bg-gray-900 text-white border-gray-900' : 'bg-white text-gray-700 border-gray-200 hover:border-gray-400')}>
      {label} <span className="opacity-60">{n}</span>
    </button>
  )

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        {chip('ALL_DIFF', 'All differences', rows.filter((r) => r.diff !== 0 || r.category === 'ENGINE_ERROR').length)}
        {CATEGORY_ORDER.filter((c) => counts[c]).map((c) => chip(c, CATEGORY_LABEL[c], counts[c]))}
        {chip('ALL', 'Everything', rows.length)}
        <span className="mx-1 text-gray-300">|</span>
        {(['all', 'base', 'aggregate'] as const).map((l) => (
          <button key={l} onClick={() => setLayer(l)}
            className={cn('text-xs px-2.5 py-1 rounded-full border', layer === l ? 'bg-gray-100 border-gray-400 text-gray-900' : 'bg-white border-gray-200 text-gray-600')}>
            {l}
          </button>
        ))}
        <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="filter table / account"
          className="ml-auto text-xs border border-gray-200 rounded px-2 py-1 w-52 focus:outline-none focus:border-gray-400" />
      </div>

      <div className="overflow-x-auto rounded-lg border border-gray-200">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-gray-200 bg-gray-50/60">
              <th className="w-6" />
              <th className="text-left py-2 px-3 text-gray-500 font-medium">Account</th>
              <th className="text-left py-2 px-3 text-gray-500 font-medium">Table</th>
              <th className="text-left py-2 px-3 text-gray-500 font-medium">Layer</th>
              <th className="text-right py-2 px-3 text-gray-500 font-medium">StarRocks</th>
              <th className="text-right py-2 px-3 text-gray-500 font-medium">Warehouse</th>
              <th className="text-right py-2 px-3 text-gray-500 font-medium">SR − WH</th>
              <th className="text-center py-2 px-3 text-gray-500 font-medium">vs prev</th>
              <th className="text-center py-2 px-3 text-gray-500 font-medium">Category</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((r) => {
              const key = `${r.accId}|${r.layer}|${r.table}`
              const isOpen = open === key
              return (
                <Fragment key={key}>
                  <tr onClick={() => setOpen(isOpen ? null : key)}
                    className={cn('border-b border-gray-100 cursor-pointer hover:bg-gray-50/70', r.category === 'DIFFERS' && 'bg-red-50/30')}>
                    <td className="pl-2 text-gray-400">{isOpen ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}</td>
                    <td className="py-2 px-3 font-mono text-xs text-gray-600">acc_{r.accId}</td>
                    <td className="py-2 px-3 font-mono text-xs text-gray-900">{r.table}</td>
                    <td className="py-2 px-3 text-xs text-gray-500">{r.layer}</td>
                    <Count value={r.starrocksCount} />
                    <Count value={r.warehouseCount} />
                    <Diff diff={r.diff} pct={r.pct} />
                    <Trend row={r} />
                    <td className="py-2 px-3 text-center"><Pill className={CATEGORY_CLASS[r.category]}>{CATEGORY_LABEL[r.category]}</Pill></td>
                  </tr>
                  {isOpen && <HistoryRow accId={r.accId} table={r.table} colSpan={9} />}
                </Fragment>
              )
            })}
            <tr>
              <td colSpan={9} className="py-1.5 px-3 text-[11px] text-gray-400">{filtered.length} of {rows.length} rows</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  )
}

// ── MV tab ────────────────────────────────────────────────────────────────────

function MvTab({ rows }: { rows: MvRow[] }) {
  const sorted = useMemo(() => [...rows].sort((a, b) => {
    const bad = (m: MvRow) => (!m.active ? 0 : m.lastRefreshState === 'FAILED' ? 1 : (m.runningMinutes ?? 0) >= 60 ? 2 : 3)
    return bad(a) - bad(b) || a.accId - b.accId || a.name.localeCompare(b.name)
  }), [rows])
  return (
    <div className="overflow-x-auto rounded-lg border border-gray-200">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-gray-200 bg-gray-50/60">
            <th className="text-left py-2 px-3 text-gray-500 font-medium">Account</th>
            <th className="text-left py-2 px-3 text-gray-500 font-medium">Materialized view</th>
            <th className="text-center py-2 px-3 text-gray-500 font-medium">Active</th>
            <th className="text-center py-2 px-3 text-gray-500 font-medium">Last refresh</th>
            <th className="text-right py-2 px-3 text-gray-500 font-medium">Running</th>
            <th className="text-left py-2 px-3 text-gray-500 font-medium">Error</th>
          </tr>
        </thead>
        <tbody>
          {sorted.map((m) => (
            <tr key={`${m.accId}|${m.name}`} className={cn('border-b border-gray-100', !m.active && 'bg-red-50/30')}>
              <td className="py-2 px-3 font-mono text-xs text-gray-600">acc_{m.accId}</td>
              <td className="py-2 px-3 font-mono text-xs text-gray-900">{m.name}</td>
              <td className="py-2 px-3 text-center">
                {m.active ? <CheckCircle className="h-4 w-4 text-green-600 inline" /> : <XCircle className="h-4 w-4 text-red-600 inline" />}
              </td>
              <td className="py-2 px-3 text-center">
                <Pill className={m.lastRefreshState === 'SUCCESS' || m.lastRefreshState === 'SKIPPED' ? 'text-green-700 bg-green-50 border-green-200'
                  : m.lastRefreshState === 'FAILED' ? 'text-red-700 bg-red-50 border-red-200' : 'text-gray-600 bg-gray-50 border-gray-200'}>
                  {m.lastRefreshState ?? 'never'}
                </Pill>
              </td>
              <td className={cn('py-2 px-3 text-right font-mono text-xs', (m.runningMinutes ?? 0) >= 60 && 'text-amber-700 font-semibold')}>
                {m.runningMinutes != null ? `${m.runningMinutes} min` : ''}
              </td>
              <td className="py-2 px-3 text-xs text-gray-600 max-w-md truncate" title={m.error ?? ''}>{m.error ?? ''}</td>
            </tr>
          ))}
          <tr><td colSpan={6} className="py-1.5 px-3 text-[11px] text-gray-400">{rows.length} materialized views</td></tr>
        </tbody>
      </table>
    </div>
  )
}

// ── Page ───────────────────────────────────────────────────────────────────────

export function ParityMonitorPage() {
  const qc = useQueryClient()
  const [selectedRunId, setSelectedRunId] = useState<string | null>(null)
  const [tab, setTab] = useState<'tables' | 'mvs' | 'errors'>('tables')

  const runs = useQuery({ queryKey: ['parity', 'runs'], queryFn: () => getRuns(48), refetchInterval: 30_000 })
  const latest = useQuery({ queryKey: ['parity', 'latest'], queryFn: getLatestRun, enabled: selectedRunId == null, refetchInterval: 60_000 })
  const selected = useQuery({ queryKey: ['parity', 'run', selectedRunId], queryFn: () => getRun(selectedRunId!), enabled: selectedRunId != null })

  const trigger = useMutation({
    mutationFn: triggerRun,
    onSettled: () => { qc.invalidateQueries({ queryKey: ['parity'] }) },
  })

  const run: RunDetail | null | undefined = selectedRunId ? selected.data : latest.data
  const loading = selectedRunId ? selected.isLoading : latest.isLoading
  const error = selectedRunId ? selected.error : latest.error
  const inProgress = runs.data?.some((r) => r.status === 'RUNNING') ?? false

  if (loading) return <LoadingState message="Loading parity runs…" />
  if (error) return <ErrorState message={error instanceof Error ? error.message : 'Failed to load parity runs'} onRetry={() => { latest.refetch(); selected.refetch() }} />

  const s = run?.header.summary ?? null
  const triggerMsg = trigger.data?.message ?? (trigger.error instanceof Error
    ? (trigger.error.message.startsWith('API Error 409') ? 'A run is already in progress.' : trigger.error.message)
    : null)

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold text-gray-900 flex items-center gap-2"><Scale className="h-5 w-5" /> Parity Monitor</h1>
          <p className="text-sm text-gray-500 mt-0.5">
            StarRocks vs Warehouse row counts, every 2 hours, across all accounts — with changes since the previous run.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <select
            value={selectedRunId ?? ''}
            onChange={(e) => setSelectedRunId(e.target.value || null)}
            className="text-xs border border-gray-200 rounded px-2 py-1.5 bg-white max-w-xs">
            <option value="">Latest completed run</option>
            {(runs.data ?? []).map((r: RunHeader) => (
              <option key={r.runId} value={r.runId}>
                {formatDate(r.startedAt)} · {r.status.toLowerCase()} · {r.trigger}{r.summary ? ` · ${r.summary.changesHigh}H/${r.summary.changesMedium}M` : ''}
              </option>
            ))}
          </select>
          <Button variant="outline" size="sm" className="gap-2" onClick={() => qc.invalidateQueries({ queryKey: ['parity'] })}>
            <RefreshCw className="h-4 w-4" /> Refresh
          </Button>
          <Button size="sm" className="gap-2" onClick={() => trigger.mutate()} disabled={trigger.isPending || inProgress}>
            <Play className="h-4 w-4" /> {inProgress ? 'Run in progress…' : 'Run now'}
          </Button>
        </div>
      </div>
      {triggerMsg && <div className="text-xs text-gray-600 -mt-3">{triggerMsg}</div>}

      {!run && (
        <div className="rounded-lg border border-gray-200 p-6 text-sm text-gray-600">
          No completed run yet. The scheduler runs every 2 hours; use <em>Run now</em> to start one.
        </div>
      )}

      {run && s && (
        <>
          {/* Run header line */}
          <div className="text-xs text-gray-500 flex flex-wrap gap-x-4 gap-y-1">
            <span>env <span className="font-mono text-gray-800">{run.header.env}</span></span>
            <span>run <span className="font-mono text-gray-800">{run.header.runId.slice(0, 8)}</span></span>
            <span>started {formatDate(run.header.startedAt)}</span>
            {run.header.finishedAt && <span>took {Math.round((new Date(run.header.finishedAt).getTime() - new Date(run.header.startedAt).getTime()) / 1000)}s</span>}
            <span>{run.header.trigger}</span>
            <span>{s.accounts} accounts</span>
            {!(s.starrocksReachable && s.warehouseReachable) && (
              <Pill className="text-red-700 bg-red-50 border-red-200"><ServerCrash className="h-3 w-3" />
                {!s.starrocksReachable && 'StarRocks unreachable'}{!s.starrocksReachable && !s.warehouseReachable && ' · '}{!s.warehouseReachable && 'Warehouse unreachable'}
              </Pill>
            )}
          </div>

          {/* Tiles */}
          <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-3">
            <Tile label="Base tables" value={`${s.baseMatch} / ${s.baseCompared}`} sub="match exactly" icon={<Database className="h-4 w-4" />}
              tone={s.baseCompared > 0 && s.baseMatch === s.baseCompared ? 'good' : 'default'} />
            <Tile label="Aggregates" value={`${s.aggregateMatch} / ${s.aggregateCompared}`} sub="match exactly" icon={<Activity className="h-4 w-4" />}
              tone={s.aggregateCompared > 0 && s.aggregateMatch === s.aggregateCompared ? 'good' : 'default'} />
            <Tile label="Unexplained" value={String(s.differs)} sub="DIFFERS rows" icon={<AlertTriangle className="h-4 w-4" />}
              tone={s.differs === 0 ? 'good' : 'bad'} />
            <Tile label="MV health" value={`${s.mvTotal - s.mvInactive - s.mvFailed} / ${s.mvTotal}`}
              sub={`${s.mvInactive} inactive · ${s.mvFailed} failed`} icon={<CheckCircle className="h-4 w-4" />}
              tone={s.mvInactive > 0 ? 'bad' : s.mvFailed > 0 ? 'warn' : 'good'} />
            <Tile label="Refresh queue" value={`${s.queuePending}`} sub={`pending · ${s.queueRunning} running`} icon={<RefreshCw className="h-4 w-4" />}
              tone={s.queuePending > 20 ? 'warn' : 'default'} />
            <Tile label="Changes" value={`${s.changesHigh}H ${s.changesMedium}M ${s.changesInfo}I`} sub="since previous run" icon={<Scale className="h-4 w-4" />}
              tone={s.changesHigh > 0 ? 'bad' : s.changesMedium > 0 ? 'warn' : 'good'} />
          </div>

          {/* Changes */}
          <section>
            <h2 className="text-sm font-semibold text-gray-800 mb-2">Changes since previous run</h2>
            <ChangesFeed changes={run.changes} />
          </section>

          {/* Tabs */}
          <section>
            <div className="flex gap-1 border-b border-gray-200 mb-3">
              {([['tables', `Tables (${run.tables.length})`], ['mvs', `Materialized views (${run.materializedViews.length})`], ['errors', `Errors (${run.errors.length})`]] as const).map(([k, label]) => (
                <button key={k} onClick={() => setTab(k)}
                  className={cn('px-3 py-2 text-sm -mb-px border-b-2', tab === k ? 'border-gray-900 text-gray-900 font-medium' : 'border-transparent text-gray-500 hover:text-gray-800')}>
                  {label}
                </button>
              ))}
            </div>
            {tab === 'tables' && <TablesTab rows={run.tables} />}
            {tab === 'mvs' && <MvTab rows={run.materializedViews} />}
            {tab === 'errors' && (
              run.errors.length === 0
                ? <div className="text-sm text-gray-500">No errors recorded for this run.</div>
                : <ul className="rounded-lg border border-gray-200 divide-y divide-gray-100 text-xs font-mono">
                    {run.errors.map((e, i) => <li key={i} className="px-3 py-2 text-gray-700 break-all">{e}</li>)}
                  </ul>
            )}
          </section>
        </>
      )}
    </div>
  )
}
